<template>
  <div class="w-full flex flex-col">
    <!-- top padding -->
    <transition name="padding">
      <div v-if="show" class="w-full" :style="{ height: `${topPadding}px`, '--padding-height': `${topPadding}px` }" />
    </transition>
    <!-- Validation Bars -->
    <transition name="bars">
      <div v-if="show" class="w-full grid gap-x-2 h-3 items-center" 
      :style="{ gridTemplateColumns: `repeat(${validationRequirements.length}, 1fr)` }">
        <div v-for="n in validationRequirements.length" :key="`validation-bar-${n}`"
        class="validation-bar" :class="{'valid': n <= numValidRequirements}" />
      </div>
    </transition>
    <!-- Validation Requirements -->
    <div v-for="(requirement, index) in computedValidations" :key="`validation-requirement-${index}`"
    class="w-full flex items-end gap-2 "
    :class="[
      alwaysShowRequirements || !allowExitAnim ? '' : requirement.visible ? 'requirement-enter' : 'requirement-leave',
      allowExitAnim ? 'h-7' : 'h-0 opacity-0'
    ]">
      <ValidityAnimatedIcon :isValid="requirement.isValid" />
      <BaseText type="label" size="sm" class="transition-colors duration-300"
      :class="requirement.isValid ? 'text-white' : 'text-neutral-alpha-650'">
        {{ requirement.label }}
      </BaseText>
    </div>
    <!-- bottom padding -->
    <transition name="padding">
      <div v-if="show" class="w-full" :style="{ height: `${bottomPadding}px`, '--padding-height': `${bottomPadding}px` }" />
    </transition>
  </div>
</template>

<script>
import ValidityAnimatedIcon from '../globals/Icons/ValidityAnimatedIcon.vue'

export default {
  name: 'ValidationVisualizer',
  components: {
    ValidityAnimatedIcon
  },
  props: {
    value: {
      required: true
    },
    validationRequirements: {
      type: Array,
      required: true
    },
    isValid: {
      type: Boolean,
      required: true
    },
    showValidator: {
      type: Boolean,
      default: true
    },
    alwaysShowRequirements: {
      type: Boolean,
      default: false
    },
    showValidBarsWhenComplete: {
      type: Boolean,
      default: true
    },
    topPadding: {
      type: Number,
      default: 8
    },
    bottomPadding: {
      type: Number,
      default: 8
    }
  },
  data () {
    return {
      animBarsExit: false,
      allowExitAnim: false // Prevents requirements exit anim on mount
    }
  },
  watch: {
    showValidator (newVal) {
      if (newVal) this.allowExitAnim = true
    },
    requirementsMet (newVal) {
      this.$emit('update:isValid', newVal)
      if (newVal) {
        setTimeout(() => { 
          if (this.requirementsMet && !this.showValidBarsWhenComplete) this.animBarsExit = true 
        }, 1)
      } 
      else this.animBarsExit = false
    }
  },
  computed: {
    computedValidations () {
      return this.validationRequirements.map(requirement => {
        const isValid = requirement.validator(this.value)
        return {
          ...requirement,
          isValid,
          visible: this.showValidator && !isValid
        }
      })
    },
    numValidRequirements () {
      return this.computedValidations.filter(validation => validation.isValid).length
    },
    requirementsMet () {
      return this.numValidRequirements === this.validationRequirements.length
    },
    show () {
      return this.alwaysShowRequirements || (this.showValidator && !this.animBarsExit)
    }
  }
}
</script>

<style scoped>
.validation-bar {
  position: relative;
  height: 4px;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.16);
  border-radius: 4px;
}
.validation-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: #00C7A9;
  border-radius: 4px;
  width: 0px;
  opacity: 0;
  transition: width 300ms ease-in-out, opacity 300ms ease-in-out;
}
.validation-bar.valid::after {
  width: 100%;
  opacity: 1;
}

.requirement-enter {
  animation-name: requirementEnter;
  animation-duration: 300ms;
  animation-timing-function: ease-in-out;
  animation-fill-mode: forwards;
}
.requirement-leave {
  animation-name: requirementLeave;
  animation-duration: 300ms;
  animation-timing-function: ease-in-out;
  animation-fill-mode: forwards;
  animation-delay: 300ms;
}
@keyframes requirementEnter {
  0% {
    opacity: 0;
    height: 0px;
    transform: translateY(6px);
  }
  100% {
    opacity: 1;
    height: 28px;
    transform: translateY(0px);
  }
}
@keyframes requirementLeave {
  0% {
    opacity: 1;
    height: 28px;
    transform: translateY(0px);
  }
  100% {
    opacity: 0;
    height: 0px;
    transform: translateY(6px);
  }
}

.bars-enter-active {
  animation: barsEnter 300ms ease-in-out forwards;
}
.bars-leave-active {
  animation: barsExit 300ms ease-in-out 300ms forwards;
}
@keyframes barsEnter {
  0% { height: 0px; opacity: 0; }
  33% { opacity: 0; }
  100% { height: 12px; opacity: 1; }
}
@keyframes barsExit {
  0% { height: 12px; opacity: 1; }
  50% { height: 12px; opacity: 0; }
  100% { height: 0px; opacity: 0; }
}

.padding-enter-active {
  animation: paddingEnter 300ms ease-in-out forwards;
}
.padding-leave-active {
  animation: paddingExit 300ms ease-in-out 300ms forwards;
}
@keyframes paddingEnter {
  0% { height: 0px; opacity: 0; }
  100% { height: var(--padding-height); opacity: 1; }
}
@keyframes paddingExit {
  0% { height: var(--padding-height); opacity: 1; }
  100% { height: 0px; opacity: 0; }
}
</style>